import { Layout } from '@/components/layout';
import { useListLogs } from '@workspace/api-client-react';
import { useAuthHeaders } from '@/lib/auth-store';
import { Activity, Search } from 'lucide-react';
import { fmtDateTime } from '@/lib/dateFormat';

export function Logs() {
  const authOpts = useAuthHeaders();
  // We can add pagination state later, requesting limit 50 for now
  const { data: logs, isLoading } = useListLogs({ request: authOpts }, { query: { queryKey: ['/api/logs', { limit: 50 }] } });

  return (
    <Layout>
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display text-gradient mb-2">Run Logs</h1>
          <p className="text-muted-foreground">Global history of all automation executions.</p>
        </div>
        
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input 
            type="text" 
            placeholder="Search logs..." 
            className="h-10 pl-10 pr-4 bg-muted/50 border border-border rounded-xl text-sm focus:outline-none focus:border-primary/50 text-foreground placeholder:text-slate-600 w-full md:w-64"
          />
        </div>
      </div>

      <div className="glass-panel rounded-2xl border border-border/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="uppercase tracking-wider text-[10px] font-bold text-muted-foreground bg-muted border-b border-border/50">
              <tr>
                <th className="px-6 py-4">Timestamp</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Flow Name</th>
                <th className="px-6 py-4">Items Processed</th>
                <th className="px-6 py-4">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {isLoading ? (
                [1,2,3,4,5].map(i => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-5"><div className="h-4 bg-muted/50 rounded w-24"></div></td>
                    <td className="px-6 py-5"><div className="h-4 bg-muted/50 rounded w-16"></div></td>
                    <td className="px-6 py-5"><div className="h-4 bg-muted/50 rounded w-32"></div></td>
                    <td className="px-6 py-5"><div className="h-4 bg-muted/50 rounded w-12"></div></td>
                    <td className="px-6 py-5"><div className="h-4 bg-muted/50 rounded w-48"></div></td>
                  </tr>
                ))
              ) : logs?.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                    <Activity className="w-8 h-8 mx-auto mb-3 opacity-20" />
                    No run logs found.
                  </td>
                </tr>
              ) : (
                logs?.map((log) => (
                  <tr key={log.id} className="hover:bg-muted/30 transition-colors group">
                    <td className="px-6 py-4 text-muted-foreground font-mono text-xs">
                      {fmtDateTime(log.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold capitalize ${
                        log.status === 'success' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 
                        log.status === 'error' ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' : 
                        'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          log.status === 'success' ? 'bg-emerald-400' : 
                          log.status === 'error' ? 'bg-rose-500' : 'bg-amber-400 animate-pulse'
                        }`} />
                        {log.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium text-foreground">
                      {log.flowName || <span className="text-slate-600 italic">Deleted Flow</span>}
                    </td>
                    <td className="px-6 py-4 text-foreground font-mono">
                      {log.itemsCount}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground max-w-xs truncate">
                      {log.message || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
